import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { compareAsc, startOfDay } from 'date-fns';
import { MatchesListPage } from '@ui/fixtures/components/MatchesListPage/MatchesListPage';
import { useIsMobile } from '../../utils/useIsMobile';
import { getCurrentMonth, getMonth, getNextMonth, getUTCDate, MonthData } from '@ui/utils/date';
import { MatchPeriodFilter, MatchSort, MatchStatusFilter, MatchStatus } from '@repo/types';
import { useMatchFiltersQuery } from '@data/matches/useMatchFiltersQuery';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useMatchDatesQuery } from '@data/matches/useMatchDatesQuery';
import { useEditableClubsQuery } from '@data/clubs/useEditableClubsQuery';
import { Helmet } from 'react-helmet-async';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';
import { useTenantFeatureFlagsQuery } from '@data/tenants/useTenantFeatureFlagsQuery';

const getStartDate = (startDate: string | null) => {
  if (!startDate) {
    return getCurrentMonth();
  } else {
    const jsDate = new Date(startDate);
    const d = getUTCDate(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate());

    return {
      ...getMonth(d),
      date: d,
    };
  }
};

export function FixturesPage() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const startDateSearchParam = searchParams.get('startDate');
  const endDateSearchParam = searchParams.get('endDate');
  const teamSearchParam = searchParams.get('team');
  const competitionSearchParam = searchParams.get('competition');
  const [updatedMonthToShow, setUpdatedMonthToShow] = useState<MonthData | null>(null);
  const currentMonth = useMemo(() => getStartDate(startDateSearchParam), [startDateSearchParam]);

  const { data: clubs, isLoading: areClubsLoading } = useEditableClubsQuery();
  const { data: matchFilters, isLoading: matchFiltersLoading } = useMatchFiltersQuery();
  const { data: tenantFeatureFlags, isLoading: isLoadingFeatureFlags } = useTenantFeatureFlagsQuery();

  const selectedCompetition = useMemo(() => {
    return matchFilters?.find((matchFilter) => matchFilter.competitions === competitionSearchParam);
  }, [matchFilters, competitionSearchParam]);

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const startDate = useMemo(() => {
    if (startDateSearchParam) {
      return startDateSearchParam;
    }

    return today > currentMonth.startDate ? today : currentMonth.startDate;
  }, [startDateSearchParam, today, currentMonth.startDate]);

  const endDate = useMemo(() => {
    if (endDateSearchParam) {
      return endDateSearchParam;
    }

    return currentMonth.endDate;
  }, [endDateSearchParam, currentMonth.endDate]);

  const {
    data: matches,
    isLoading: matchesLoading,
    isFetchedAfterMount: areMatchesFetchedAfterMount,
    error: matchesError,
  } = useMatchesQuery(
    {
      limit: 1000,
      period: MatchPeriodFilter.Custom,
      startDate,
      endDate,
      sort: MatchSort.ASC,
      status: MatchStatusFilter.FIXTURE,
      teamId: teamSearchParam,
      competition: selectedCompetition?.competitions ?? matchFilters?.[0]?.competitions,
    },
    {
      enabled: !!matchFilters,
    }
  );

  const { data: matchDates, isLoading: matchDatesLoading } = useMatchDatesQuery(
    {
      startDate: today,
      status: MatchStatusFilter.FIXTURE,
      teamId: teamSearchParam,
      competition: selectedCompetition?.competitions ?? matchFilters?.[0]?.competitions,
    },
    {
      enabled: !!matchFilters,
    }
  );

  const matchesWithoutPostponed = useMemo(() => {
    const today = startOfDay(new Date());

    return matches?.items.filter((match) => {
      if (match.status !== MatchStatus.Postponed && match.status !== MatchStatus.Abandoned) {
        return true;
      }

      const matchDate = new Date(match.date);

      if (compareAsc(today, matchDate) <= 0) {
        return true;
      }
      return false;
    });
  }, [matches]);

  useEffect(() => {
    if (
      !matchesLoading &&
      areMatchesFetchedAfterMount &&
      !startDateSearchParam &&
      matchesWithoutPostponed?.length === 0
    ) {
      const nextMonth = getNextMonth(getCurrentMonth().date);
      setSearchParams(
        (prev) => {
          prev.set('startDate', nextMonth.startDate);
          prev.set('endDate', nextMonth.endDate);
          return prev;
        },
        { replace: true }
      );

      setUpdatedMonthToShow(nextMonth);
    }
  }, [
    areMatchesFetchedAfterMount,
    matchesLoading,
    matchesWithoutPostponed?.length,
    currentMonth,
    startDateSearchParam,
    setSearchParams,
  ]);

  if (matchFiltersLoading || matchesLoading || matchDatesLoading || areClubsLoading || isLoadingFeatureFlags) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (matchesError || !matchFilters || !matchesWithoutPostponed || !clubs || !tenantFeatureFlags) {
    return <NotFoundPage />;
  }

  const metaTitle = `${import.meta.env.VITE_SITE_NAME} Fixtures & Live Matches Season 2025/26`;
  const metaDescription = `View the ${
    import.meta.env.VITE_ENTITY_NAME
  } fixtures for the 2025/26 season, visit the official website of the ${import.meta.env.VITE_SITE_NAME}.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <MatchesListPage
        title={'Fixtures'}
        isMobile={isMobile}
        matchFilters={matchFilters}
        selectedCompetition={selectedCompetition ?? matchFilters[0]}
        matches={matchesWithoutPostponed}
        clubs={clubs.items}
        currentMonth={updatedMonthToShow ?? currentMonth}
        monthSwitcherMode="FIXTURES"
        featureFlags={tenantFeatureFlags}
        sortByDate={'ASC'}
        matchDates={matchDates}
      />
    </>
  );
}
